import { Injectable, computed, signal } from '@angular/core';

/** Must match the backend gate in TransfloAccessToken.IsGlobalAdmin. */
const INTERNAL_USER_TYPE = 'INTERNAL';
const SUPER_ADMIN_PORTAL_ROLE = 'TRANSFLO_SUPER_ADMIN';
const WORKFLOW_STUDIO_APPLICATION = 'WORKFLOW_STUDIO';

interface ClaimsInfo {
  customerId: string;
  userId: string;
  firstName: string;
  lastName: string;
  isImpersonating?: boolean;
  userType?: string;
  portalRole?: string;
  /** Raw claim: a JSON-encoded array of "APPLICATION:ROLE" strings. */
  applicationRoles?: string;
}

/** Tolerates a malformed claim rather than breaking sign-in, matching the backend parser. */
const parseApplicationRoles = (raw?: string): string[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((r): r is string => typeof r === 'string')
      : [];
  } catch {
    return [];
  }
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userInfo = signal<ClaimsInfo | null>(null);

  /**
   * True only for Transflo super-admins acting via impersonation.
   */
  public isImpersonating = computed(
    () => this.userInfo()?.isImpersonating ?? false
  );

  /**
   * A Transflo super-admin working without impersonating a customer. Such a session has no
   * customer at all and operates only on global templates.
   *
   * This is the single place in the app that inspects identity - everything else asks
   * StudioCapabilitiesService what is allowed, not who the user is.
   */
  public isGlobalAdmin = computed(() => {
    const claims = this.userInfo();
    if (!claims) return false;

    const hasWorkflowStudio = parseApplicationRoles(
      claims.applicationRoles
    ).some(role => role.split(':')[0] === WORKFLOW_STUDIO_APPLICATION);

    return (
      !claims.customerId &&
      claims.userType === INTERNAL_USER_TYPE &&
      claims.portalRole === SUPER_ADMIN_PORTAL_ROLE &&
      hasWorkflowStudio
    );
  });

  public setUser(user: ClaimsInfo | null) {
    this.userInfo.set(user);
  }

  get initials() {
    if (!this.userInfo()) return;
    return `${this.userInfo()?.firstName?.charAt(0) || ''}${this.userInfo()?.lastName?.charAt(0) || ''}`.trim();
  }
}
